import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../../firebase/config';
import SharedHeader from '../../../../components/SharedHeader';
import DashboardFooter from '../../../../components/DashboardFooter';
import UserGeoHeatmap from './heat_map/usr_geo_heatmap';
import FinancialHeatMap from './heat_map/financial_heat';
import CountryPerform from './heat_map/country_perform';
// # Import map_iso utilities
import { 
  getCountryISO, 
  getCountryNameFromISO, 
  formatCountryDataForHeatMap,
  getCountryData,
  groupCountryDataByRegion,
  getISOFromAnyFormat
} from './heat_map/map_iso';
import './heat_map.css';

// # Heat Map Component
const HeatMap = () => {
  // # Initialize navigation
  const navigate = useNavigate();
  
  // # State for active tab
  const [activeTab, setActiveTab] = useState('user'); // 'user', 'financial', or 'performance'
  
  // # Add state for sorting and filtering
  const [sortConfig, setSortConfig] = useState({ key: 'count', direction: 'desc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('All');
  
  // # State for analytics data
  const [analyticsData, setAnalyticsData] = useState({
    usersByCountry: {},
    usersByISO: {},
    usersByRegion: {},
    totalUsers: 0
  });
  
  // # Loading state
  const [isLoading, setIsLoading] = useState(true);
  
  // # Fetch user data on component mount
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setIsLoading(true);
        const startTime = performance.now();
        
        // # Get users collection
        const usersRef = collection(db, 'users');
        const usersSnap = await getDocs(usersRef);
        
        console.log('Heat Map - Fetching user data:', {
          operation: 'fetch_users_geo',
          usersCount: usersSnap.size,
          timestamp: new Date().toISOString(),
          timeElapsed: `${(performance.now() - startTime).toFixed(2)}ms`
        });
        
        // # Process user data
        const usersByCountry = {};
        let totalUsers = 0;
        
        usersSnap.forEach(doc => {
          const userData = doc.data();
          const country = userData.nationality || 'Unknown';
          
          // # Increment country count
          usersByCountry[country] = (usersByCountry[country] || 0) + 1;
          totalUsers++;
        });
        
        console.log('Raw user data by country:', usersByCountry);
        
        // # Group data by region
        const usersByRegion = groupCountryDataByRegion(usersByCountry);
        
        // # Format data for heat map using enhanced map_iso utilities
        const usersByISO = formatCountryDataForHeatMap(usersByCountry, {
          useISO: true,
          defaultValue: null,
          includeEmptyCountries: false,
          preserveUnmappedCountries: true
        }) || {};
        
        // Filter out any null or 0 values
        const filteredUsersByISO = Object.entries(usersByISO).reduce((acc, [key, value]) => {
          if (value && value > 0) {
            acc[key] = value;
          }
          return acc;
        }, {});
        
        console.log('Filtered user data by ISO:', filteredUsersByISO);
        
        // # Update state with processed data
        setAnalyticsData({
          usersByCountry: usersByCountry || {},
          usersByISO: filteredUsersByISO,
          usersByRegion: usersByRegion || {},
          totalUsers
        });
        
        console.log('Fetched user data:', { 
          usersByCountry, 
          usersByISO,
          usersByRegion,
          totalUsers 
        });
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching user data:', error);
        setIsLoading(false);
      }
    };
    
    fetchUserData();
  }, []);
  
  // # Sort countries by user count
  const getSortedCountries = () => {
    return Object.entries(analyticsData.usersByCountry)
      .sort(([, a], [, b]) => b - a)
      .map(([country, count]) => {
        // # Get ISO code and additional country data using enhanced function
        const isoCode = getISOFromAnyFormat(country);
        
        // # Get country data for region information
        const countryData = getCountryData(isoCode);
        const region = countryData?.region || 'Unknown';
        
        // # Use the original country name from the data for display
        // # This preserves the actual country name instead of relying on ISO lookup
        const displayName = country;
        
        return {
          country: displayName,
          isoCode,
          region,
          count,
          percentage: ((count / analyticsData.totalUsers) * 100).toFixed(1)
        };
      });
  };

  // # Get countries grouped by region
  const getCountriesByRegion = () => {
    const regions = {};
    
    getSortedCountries().forEach(item => {
      if (!regions[item.region]) {
        regions[item.region] = [];
      }
      regions[item.region].push(item);
    });
    
    return regions;
  };

  // # Handle sorting
  const handleSort = (key) => {
    setSortConfig((prevConfig) => {
      if (prevConfig.key === key) {
        return {
          ...prevConfig,
          direction: prevConfig.direction === 'asc' ? 'desc' : 'asc'
        };
      }
      return { key, direction: 'desc' };
    });
  };

  // # Get unique regions for filter dropdown
  const getUniqueRegions = () => {
    const regions = new Set(getSortedCountries().map(item => item.region));
    return ['All', ...Array.from(regions)].filter(Boolean);
  };

  // # Sort and filter countries
  const getFilteredAndSortedCountries = () => {
    let filteredData = getSortedCountries();

    // # Apply region filter
    if (selectedRegion !== 'All') {
      filteredData = filteredData.filter(item => item.region === selectedRegion);
    }

    // # Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filteredData = filteredData.filter(item =>
        item.country.toLowerCase().includes(searchLower) ||
        item.region.toLowerCase().includes(searchLower)
      );
    }

    // # Apply sorting
    filteredData.sort((a, b) => {
      let comparison = 0;
      if (sortConfig.key === 'country') {
        comparison = a.country.localeCompare(b.country);
      } else if (sortConfig.key === 'region') {
        comparison = a.region.localeCompare(b.region);
      } else if (sortConfig.key === 'count') {
        comparison = a.count - b.count;
      } else if (sortConfig.key === 'percentage') {
        comparison = parseFloat(a.percentage) - parseFloat(b.percentage);
      }
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });

    return filteredData;
  };

  return (
    <div className="ht_mp_mn_admin-page">
      <SharedHeader />
      
      <main className="ht_mp_mn_admin-content">
        <div className="ht_mp_mn_header">
          <h1>Geographic Analytics</h1>
          <p>Visualize user and financial data across different countries</p>
        </div>
        
        {/* Tab Navigation */}
        <div className="ht_mp_mn_tabs">
          <button 
            className={`ht_mp_mn_tab ${activeTab === 'user' ? 'ht_mp_mn_active' : ''}`}
            onClick={() => setActiveTab('user')}
          >
            User Distribution
          </button>
          <button 
            className={`ht_mp_mn_tab ${activeTab === 'financial' ? 'ht_mp_mn_active' : ''}`}
            onClick={() => setActiveTab('financial')}
          >
            Financial Distribution
          </button>
          <button 
            className={`ht_mp_mn_tab ${activeTab === 'performance' ? 'ht_mp_mn_active' : ''}`}
            onClick={() => setActiveTab('performance')}
          >
            Country Performance
          </button>
        </div>
        
        {/* Tab Content */}
        <div className="ht_mp_mn_tab-content">
          {activeTab === 'user' && (
            <div className="ht_mp_mn_tab-panel">
              <div className="ht_mp_mn_panel-header">
                <h2>User Geographic Distribution</h2>
                <p>Visualize where your users are located around the world</p>
              </div>
              
              {/* Map Container */}
              <div className="ht_mp_mn_map-container">
                {isLoading ? (
                  <div className="ht_mp_mn_loading">Loading map data...</div>
                ) : (
                  <UserGeoHeatmap 
                    data={analyticsData.usersByISO || {}} 
                    totalUsers={analyticsData.totalUsers}
                    key={`geo-map-${Object.keys(analyticsData.usersByISO || {}).length}`} 
                  />
                )}
              </div>
              
              {/* Country Stats Table with Filters */}
              <div className="ht_mp_mn_stats-container">
                <h3>Users by Country</h3>
                
                {/* Add filters */}
                <div className="ht_mp_mn_filters">
                  <input
                    type="text"
                    placeholder="Search countries..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="ht_mp_mn_search"
                  />
                  <select
                    value={selectedRegion}
                    onChange={(e) => setSelectedRegion(e.target.value)}
                    className="ht_mp_mn_region-filter"
                  >
                    {getUniqueRegions().map(region => (
                      <option key={region} value={region}>{region}</option>
                    ))}
                  </select>
                </div>

                <div className="ht_mp_mn_country-stats">
                  <table className="ht_mp_mn_stats-table">
                    <thead>
                      <tr>
                        <th 
                          className="ht_mp_mn_sortable"
                          onClick={() => handleSort('country')}
                        >
                          Country {sortConfig.key === 'country' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </th>
                        <th 
                          className="ht_mp_mn_sortable"
                          onClick={() => handleSort('region')}
                        >
                          Region {sortConfig.key === 'region' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </th>
                        <th 
                          className="ht_mp_mn_sortable"
                          onClick={() => handleSort('count')}
                        >
                          Users {sortConfig.key === 'count' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </th>
                        <th 
                          className="ht_mp_mn_sortable"
                          onClick={() => handleSort('percentage')}
                        >
                          Percentage {sortConfig.key === 'percentage' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredAndSortedCountries().map(({ country, region, count, percentage }) => (
                        <tr key={country}>
                          <td>{country}</td>
                          <td>{region}</td>
                          <td>{count}</td>
                          <td>{percentage}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'financial' && (
            <div className="ht_mp_mn_tab-panel">
              <div className="ht_mp_mn_panel-header">
                <h2>Financial Geographic Distribution</h2>
                <p>Visualize revenue and product sales by country</p>
              </div>
              <FinancialHeatMap />
            </div>
          )}

          {activeTab === 'performance' && (
            <div className="ht_mp_mn_tab-panel">
              <CountryPerform />
            </div>
          )}
        </div>
      </main>
      
      <DashboardFooter />
    </div>
  );
};

export default HeatMap; 